import { setlist } from '@/utils/typesense';
import {
  DEFAULT_VIDEO_ENGAGEMENT_SLUG,
  EXCLUDE_LUTS_FILTER,
  LUTS_FILTER,
  MOTION_GRAPHICS_CATEGORIES,
  MOTION_GRAPHICS_COLLECTION,
  MUSIC_CATEGORIES,
  MUSIC_COLLECTION,
  MUSIC_COLLECTIONS,
  MUSIC_CONTRIBUTORS,
  MUSIC_QUALIFIED_KEYWORDS,
  MUSIC_TAGS,
  SFX_CATEGORIES,
  SFX_COLLECTION,
  SFX_COLLECTIONS,
  SFX_TAGS,
  STOCK_VIDEO_CATEGORIES,
  STOCK_VIDEO_COLLECTION,
  STOCK_VIDEO_INCLUDE_FIELDS,
  VIDEO_ENGAGEMENT_SORT,
  VIDEO_RECENCY_TIE_BREAKER,
  composeStockVideoSort,
  getCategoryStatusFilter,
  getVideoEngagementJoinFilter,
} from '@music-vine/setlist';
import { isProfane } from '@/utils/profanityFilter';
import {
  LUTS_MG_COLLECTION_MARKER,
  mergeCollectionHits,
} from '@/features/GlobalSearch/utils/mergeCollections';

import { ASSET_TYPE } from '@/types/asset';
import type { AssetType } from '@/features/GlobalSearch/types/index';
import type { GenericSearchResult } from '@/features/SearchPage/types';
import type { SearchResponse } from '@music-vine/setlist';

const normalizeString = (str: string): string => {
  return str
    .toLowerCase()
    .replace(/[\s-]+/g, '') // Remove all whitespace and hyphens
    .trim();
};

export const getSuggestedResults = async (
  query: string,
  assetType: AssetType,
  isSubscribedUser = false
): Promise<SearchResponse<GenericSearchResult>[]> => {
  if (isProfane(query)) {
    return [];
  }

  const params: {
    searches: {
      q: string;
      collection: string;
      query_by: string;
      query_by_weights?: string;
      per_page: number;
      min_len_1typo?: number;
      num_typos?: number;
      filter_by?: string;
      sort_by?: string;
      include_fields?: string;
      exclude_fields?: string;
    }[];
  } = { searches: [] };

  switch (assetType) {
    case 'sfx':
      params.searches = [
        {
          q: query,
          collection: SFX_COLLECTION,
          query_by: 'name, keywords',
          per_page: 50,
        },
        {
          q: query,
          collection: SFX_COLLECTIONS,
          query_by: 'name, keywords',
          per_page: 50,
          min_len_1typo: 3,
          num_typos: 2,
        },
        {
          q: query,
          collection: SFX_CATEGORIES,
          query_by: 'name',
          per_page: 50,
          min_len_1typo: 3,
          num_typos: 2,
        },
        {
          q: query,
          collection: SFX_TAGS,
          query_by: 'name',
          per_page: 50,
          min_len_1typo: 3,
          num_typos: 2,
        },
      ];
      break;
    case 'motion':
      params.searches = [
        {
          q: query,
          collection: MOTION_GRAPHICS_COLLECTION,
          query_by: 'name, keywords',
          per_page: 50,
          min_len_1typo: 3,
          num_typos: 2,
          // motiongraphics.v2 also holds LUTs (is_lut:true); scope the MG
          // search to the MG side so LUTs don't leak into motion results.
          filter_by: EXCLUDE_LUTS_FILTER,
        },
        {
          q: query,
          collection: MOTION_GRAPHICS_CATEGORIES,
          query_by: 'name,description',
          per_page: 50,
          min_len_1typo: 3,
          num_typos: 2,
        },
      ];
      break;
    case 'luts':
      params.searches = [
        {
          q: query,
          collection: MOTION_GRAPHICS_COLLECTION,
          query_by: 'name, keywords',
          per_page: 50,
          min_len_1typo: 3,
          num_typos: 2,
          filter_by: LUTS_FILTER,
        },
      ];
      break;
    case ASSET_TYPE.STOCK_VIDEO:
      params.searches = [
        {
          q: query,
          collection: STOCK_VIDEO_COLLECTION,
          query_by: 'title,keywords,description',
          per_page: 50,
          min_len_1typo: 3,
          num_typos: 2,
          filter_by: getVideoEngagementJoinFilter(
            isSubscribedUser ? 'paid' : 'free',
            DEFAULT_VIDEO_ENGAGEMENT_SLUG
          ),
          include_fields: STOCK_VIDEO_INCLUDE_FIELDS,
          sort_by: composeStockVideoSort([
            VIDEO_ENGAGEMENT_SORT,
            '_text_match:desc',
            VIDEO_RECENCY_TIE_BREAKER,
          ]),
        },
        {
          q: query,
          collection: STOCK_VIDEO_CATEGORIES,
          query_by: 'name',
          filter_by: getCategoryStatusFilter(false),
          per_page: 50,
          min_len_1typo: 3,
          num_typos: 2,
        },
      ];
      break;
    default:
      params.searches = [
        {
          q: query,
          collection: MUSIC_COLLECTION, //This is tracks
          query_by: 'name, keywords',
          query_by_weights: 'name:10, keywords:3',
          per_page: 50,
          min_len_1typo: 3,
          num_typos: 2,
        },
        {
          q: query,
          collection: MUSIC_CONTRIBUTORS,
          query_by: 'name',
          per_page: 50,
          min_len_1typo: 3,
          num_typos: 2,
        },
        {
          q: query,
          collection: MUSIC_COLLECTIONS, //This is collections
          query_by: 'name, keywords',
          query_by_weights: 'name:100, keywords:3',
          per_page: 50,
          min_len_1typo: 3,
          num_typos: 2,
        },
        {
          q: query,
          collection: MUSIC_TAGS,
          query_by: 'name',
          per_page: 50,
          min_len_1typo: 3,
          num_typos: 2,
        },
        {
          q: query,
          collection: MUSIC_CATEGORIES,
          query_by: 'name',
          per_page: 50,
          min_len_1typo: 3,
          num_typos: 2,
        },
        {
          q: query,
          collection: MUSIC_QUALIFIED_KEYWORDS,
          query_by: 'name',
          per_page: 50,
          min_len_1typo: 3,
          num_typos: 2,
        },
      ];
      break;
  }

  const search = (await setlist.multiSearch(params, { use_cache: true })) as {
    results: SearchResponse<GenericSearchResult>[];
  };

  // LUT category rows live in the shared `motiongraphicstyles` collection
  // with no boolean flag to filter on server-side — their `/luts/...`
  // relative_url is the only marker — so drop them from the motion
  // categories here before empty results are removed.
  // TODO: once the indexer adds an is_lut flag to motiongraphicstyles,
  // migrate this to a server-side filter_by like the asset queries.
  if (assetType === 'motion') {
    for (const result of search?.results ?? []) {
      if (
        result.request_params?.collection_name === MOTION_GRAPHICS_CATEGORIES
      ) {
        result.hits = result.hits?.filter(
          (hit) => !hit.document.relative_url?.startsWith('/luts')
        );
      }
    }
  }

  // Remove empty results.
  const clearedList = search?.results?.filter(
    (result) => result.hits?.length && result.hits?.length > 0
  );

  // LUTs share the motion graphics collection, so the raw response is labelled
  // `motiongraphics.v2`. Retag it before the merge so it routes to the LUTs
  // section instead of colliding with motion assets.
  if (assetType === 'luts') {
    for (const result of clearedList) {
      if (
        result.request_params?.collection_name === MOTION_GRAPHICS_COLLECTION
      ) {
        result.request_params.collection_name = LUTS_MG_COLLECTION_MARKER;
      }
    }
  }

  const mergedCollections = mergeCollectionHits([...clearedList]);

  // Define collection priority for when scores are equal.
  // Higher number = higher priority.
  const collectionsValue = {
    'popular music searches': 1,
    'popular sfx searches': 2,
    'popular motion graphics searches': 3,
    'popular stock video searches': 4,
    playlists: 5,
    'sfx collections': 6,
    types: 7,
    artists: 8,
    tracks: 9,
    sfx: 9,
    'motion assets': 10,
    'stock video assets': 11,
    'luts assets': 12,
  };

  const sortedCollections = mergedCollections.sort((a, b) => {
    const aCollection = a.request_params?.collection_name?.toLowerCase() as
      | keyof typeof collectionsValue
      | undefined;

    const bCollection = b.request_params?.collection_name?.toLowerCase() as
      | keyof typeof collectionsValue
      | undefined;

    if (!(aCollection && bCollection)) {
      return 0;
    }

    const aCollectionValue = collectionsValue[aCollection];
    const bCollectionValue = collectionsValue[bCollection];

    return aCollectionValue - bCollectionValue;
  });

  return sortedCollections;
};
